import React, { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Card from '@mui/joy/Card';
import Typography from '@mui/joy/Typography';
import IconButton from '@mui/joy/IconButton';
import Chip from '@mui/joy/Chip';
import { ChevronLeft, ChevronRight, Handshake, FileText, FileSpreadsheet, Trash2 } from 'lucide-react';
import CreateJobButton from './CreateJobButton';
import './JobCardGrid.css';
import { Job } from '../../models/Job.interface';
import { Attachment } from '../../models/Attachment.interface';
import { formatDate } from '../../utils/dateUtils';

const LoadingCard = () => {
  return (
    <Card className="job-card">

      {/* Title and date placeholders */}
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div className="flex-grow-1">
          <p className="card-title placeholder-glow mb-2">
            <span className="placeholder col-8"></span>
          </p>
          <p className="card-text placeholder-glow mb-0">
            <span className="placeholder col-4"></span>
          </p>
        </div>
        <div className="placeholder-glow">
          <span className="placeholder rounded-circle" style={{ width: '32px', height: '32px' }}></span>
        </div>
      </div>

      {/* Image placeholder */}
      <div className="placeholder-glow mb-3">
        <span className="placeholder w-100" style={{ height: '200px' }}></span>
      </div>

      {/* Address placeholder */}
      <p className="card-text placeholder-glow mb-2">
        <span className="placeholder col-7"></span>
      </p>

      {/* Summary placeholder */}
      <p className="card-text placeholder-glow mb-3">
        <span className="placeholder col-12"></span>
        <span className="placeholder col-10"></span>
      </p>

      {/* Status and button placeholders */}
      <div className="d-flex justify-content-between align-items-center">
        <span className="placeholder rounded-pill col-3"></span>
        <span className="placeholder col-4" style={{ height: '36px' }}></span>
      </div>
    </Card>
  );
};

interface JobCardProps {
  job: ExtendedJob;
  onDelete: (jobId: string) => void;
  onCardClick?: (jobId: string) => void;
  onJobCreated?: () => void;
}

interface ExtendedJob extends Job {
  hasViewed?: boolean;
  matchedAt?: Date;
}

interface JobCardGridProps {
  jobs: ExtendedJob[];
  onDeleteJob: (jobId: string) => void;
  isLoading?: boolean;
  onJobCreated?: () => void;
  onCardClick?: (jobId: string) => void;
}

const JobCard: React.FC<JobCardProps> = ({ job, onDelete, onCardClick, onJobCreated }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const isAddJobCard = job.id === 'add-job-card';
  const location = useLocation();
  const isContractorDashboard = location.pathname === '/dashboard-contractor';

  // Modify card click handler
  const handleCardClick = (e: React.MouseEvent) => {
    if (!isAddJobCard && onCardClick) {
      onCardClick(job.id);
    }
  };
  const handleClick = () => {
    if (onCardClick) {
      onCardClick(job.id);
    }
  };

  const getFileExtension = (url: string): string => {
    const fileName = url.split('/').pop()?.split('?')[0] || '';
    return fileName.split('.').pop()?.toLowerCase() || '';
  };
  /*
    const imageAttachments = useMemo(() => {
      if (!job.jobAttachments) return [];
      return job.jobAttachments.filter((url: string) =>
        ['jpg', 'jpeg', 'png', 'gif', 'heic'].includes(getFileExtension(url))
      );
    }, [job.jobAttachments]);
  */
  const imageAttachments = useMemo(() => {
    return job.jobAttachments?.filter(attachment =>
      /\.(jpg|jpeg|png|gif|heic)/i.test(attachment.url)
    ) || [];
  }, [job.jobAttachments]);

  // AttachmentGallery.tsx updates
  interface AttachmentGalleryProps {
    attachments: Attachment[];
  }

  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (imageAttachments.length > 0) {
      setCurrentImageIndex(prev => (prev + 1) % imageAttachments.length);
    }
  };

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (imageAttachments.length > 0) {
      setCurrentImageIndex(prev => (prev - 1 + imageAttachments.length) % imageAttachments.length);
    }
  };

  return (
    <Card className="job-card" onClick={handleClick}>
      <div className="job-card-header">
        <Typography className="job-title">
          {job.jobTitle}
          {job.hasViewed !== undefined && (
            <Chip
              size="sm"
              variant="soft"
              color={job.hasViewed ? "neutral" : "primary"}
              sx={{ ml: 1 }}
            >
              {job.hasViewed ? "Viewed" : "New"}
            </Chip>
          )}
        </Typography>
        <Typography className="created-date">
          <>
            Created: {formatDate(job.dtCreated)};
            {job.matchedAt && (
              <Typography level="body-sm" className="matched-date" style={{ float: "right" }}>
                Matched: {new Date(job.matchedAt).toLocaleDateString()}
              </Typography>
            )}
          </>
        </Typography>


        {!isAddJobCard && onDelete && (
          <IconButton
            aria-label="delete job"
            variant="plain"
            className="delete-button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(job.id);
            }}
          >
            <Trash2 />
          </IconButton>
        )}
      </div>


      <div className="image-container">
        <div className="image-wrapper">
          {imageAttachments.length > 0 ? (
            <>
              <img
                src={imageAttachments[currentImageIndex].url}
                alt={`Job ${currentImageIndex + 1}`}
                className="job-image"
              />
              {imageAttachments.length > 1 && (
                <div className="image-controls">
                  <IconButton
                    size="md"
                    variant="soft"
                    onClick={prevImage}
                    className="nav-button"
                  >
                    <ChevronLeft />
                  </IconButton>
                  <IconButton
                    size="md"
                    variant="soft"
                    onClick={nextImage}
                    className="nav-button"
                  >
                    <ChevronRight />
                  </IconButton>
                </div>
              )}
              <Typography
                level="body-xs"
                className="image-counter"
              >
                {currentImageIndex + 1} / {imageAttachments.length}
              </Typography>
            </>
          ) : (
            <div className="no-image">
              <Typography level="body-sm">No images available</Typography>
            </div>
          )}
        </div>
      </div>

      <div className="location-summary-section">

        {!isAddJobCard ? (
          <Typography className="job-location">
            📍 {job.propertyAddress?.address1}, {job.propertyAddress?.city}, {job.propertyAddress?.state} {job.propertyAddress?.zipCode}
          </Typography>
        ) : (
          <Typography className="job-location">
            📍 Your project address

          </Typography>
        )}

        <Typography className="job-summary">
          {job.jobSummary}
        </Typography>
      </div>

      <div className="card-footer">


        {isAddJobCard ? (
          <CreateJobButton
            variant="info"
            className="w-100 create-job"
          />
        ) : (
          <>
            {job.contractId ? (
              <Link to={`/contract/${job.contractId}`} className="btn btn-success me-2 view-contract-button">
                <Handshake /> View Contract
              </Link>
            ) : (
              <Link to={`/job-detail/${job.id}?tab=bids`} className="btn btn-primary me-2 view-bids-button">
                <FileSpreadsheet /> {isContractorDashboard ? 'Submit / Edit Bid' : `View Bids ${job.numBids ? `(${job.numBids})` : ''}`}
              </Link>
            )}

            <Link to={`/job-detail/${job.id}`} className="btn btn-secondary me-2 view-details-button">
              <FileText /> View Job Details
            </Link>
          </>
        )}

      </div>
    </Card>
  );
};

const JobCardGrid = ({ jobs, onDeleteJob, isLoading = false, onJobCreated }: JobCardGridProps) => {
  const placeholders = Array(8).fill(null);

  if (isLoading) {
    return (
      <div className="job-card-grid">
        {placeholders.map((_, index) => (
          <LoadingCard key={`placeholder-${index}`} />
        ))}
      </div>
    );
  }

  return (
    <div className="job-card-grid">
      {jobs.map((job) => (
        <JobCard
          key={job.id}
          job={job}
          onDelete={onDeleteJob}
          onJobCreated={onJobCreated}
        />
      ))}
    </div>
  );
};

export default JobCardGrid;